import { Student } from '../types/student';
import axiosInstance from '../api/apiInterceptor';
import { AxiosResponse } from 'axios';
import {STUDENT_API_ENDPOINT_WITH_FILTER,STUDENT_API_ENDPOINT,ACADEMICYEAR_API_ENDPOINT} from "../../../constants/constants"
import {getUserDetails} from "../../../lib/getUserDetails";

interface AttendancePayload {
  schoolId?:number;
  classSectionId?: number;
  attnDate?: string;
}

// Interface for role-based filtering
interface RoleBasedFilters {
  schoolId: number;
  classSectionIds?: number[];
  role: 'Teacher' | 'Headmaster';
}

const extractErrorMessage = (error: any): string => {
  if (error?.response?.data?.message) {
    return error.response.data.message;
  }
  
  if (error?.response?.data?.error) {
    return error.response.data.error;
  }
  
  if (error?.response?.data?.details) {
    return error.response.data.details;
  }
  
  if (error?.message) {
    return error.message;
  }
  
  return 'An unexpected error occurred';
};

class ApiError extends Error {
  public originalError: any;
  public statusCode?: number;
  
  constructor(message: string, originalError: any) {
    super(message);
    this.name = 'ApiError';
    this.originalError = originalError;
    this.statusCode = originalError?.response?.status;
  }
}

// Helper function to extract role-based filters from user data
export const getRoleBasedFilters = (): RoleBasedFilters => {
  try {
    const userData = getUserDetails();
    console.log("User data for role filtering:", userData);
    
    if (!userData || userData.length === 0) {
      throw new Error("No user data found");
    }

    const user = userData[0];
    const role = user.role;

    if (role === 'Headmaster') {
      // Headmaster sees all students in their school
      const schoolId = user.relatedEntity.schools.id;
      console.log("Headmaster - School ID:", schoolId);
      
      return {
        schoolId,
        role: 'Headmaster'
      };
    } else if (role === 'Teacher') {
      // Teacher sees only students from their assigned class sections
      const schoolId = user.relatedEntity.schools.id;
      const schoolClasses = user.relatedEntity.schoolClasses || [];
      
      // Extract all class section IDs the teacher is assigned to
      const classSectionIds: number[] = [];
      schoolClasses.forEach((schoolClass: any) => {
        if (schoolClass.classSection && Array.isArray(schoolClass.classSection)) {
          schoolClass.classSection.forEach((section: any) => {
            if (section.id) {
              classSectionIds.push(section.id);
            }
          });
        }
      });
      return {
        schoolId,
        classSectionIds,
        role: 'Teacher'
      };
    } else {
      throw new Error(`Unsupported role: ${role}`);
    }
  } catch (error) {
    console.error("Error extracting role-based filters:", error);
    throw error;
  }
};

// Updated fetchStudentsApi with role-based filtering
export const fetchStudentsApi = async (): Promise<Student[]> => {
  try {
    const filters = getRoleBasedFilters();
    
    if (filters.role === 'Headmaster') {
      // Headmaster: Fetch all students from the school
      const response: AxiosResponse<any> = await axiosInstance.get(
        `${STUDENT_API_ENDPOINT_WITH_FILTER}|${filters.schoolId}&limit=1000`
      );
      return response.data.data;
    } else if (filters.role === 'Teacher') {
      if (!filters.classSectionIds || filters.classSectionIds.length === 0) {
        console.warn("Teacher has no assigned class sections");
        return [];
      }

      // Build filter string for multiple class sections
      const classSectionFilter = filters.classSectionIds
        .map(id => `classSectionId|${id}`)
        .join(',');
      
      const filterString = `${classSectionFilter}`;
      
      const response: AxiosResponse<any> = await axiosInstance.get(
        `${STUDENT_API_ENDPOINT}?filter=${filterString}&limit=1000`
      );
      return response.data.data || [];
    } else {
      throw new Error(`Unsupported role: ${filters.role}`);
    }
  } catch (error: any) {
    console.error('Error fetching students:', error);
    const message = extractErrorMessage(error);
    throw new ApiError(message, error);
  }
};

export const fetchStudentsNewApi = async (payload?: AttendancePayload): Promise<any[]> => {
  try {
    let url = STUDENT_API_ENDPOINT;
    
    const filterParams = []; 
    
    if (payload?.classSectionId) {
      filterParams.push(`classSectionId|${payload.classSectionId}`);
    }
    
    if (payload?.schoolId) {
      filterParams.push(`schoolId|${payload.schoolId}`);
    }
    
    if (filterParams.length > 0) {
      url += `?filter=${filterParams.join(',')}&limit=1000`;
    } else {
      url += `?limit=1000`;
    }
    
    const response: AxiosResponse<any> = await axiosInstance.get(url);
    return response.data?.data || [];
    
  } catch (error: any) {
    // Handle 404 or empty results gracefully
    if (error?.response?.status === 404 || 
        error?.response?.status === 400 ||
        error?.message?.toLowerCase().includes('not found')) {
      return [];
    }
    
    console.error('Error fetching students:', error);
    const message = extractErrorMessage(error);
    throw new ApiError(message, error);
  }
};

export const addStudentApi = async (studentData: Omit<Student, 'id'>): Promise<Student> => {
  try {
    const response: AxiosResponse<Student> = await axiosInstance.post(STUDENT_API_ENDPOINT, studentData);
    return response.data;
  } catch (error: any) {
    console.error('Error adding student:', error);
    const message = extractErrorMessage(error);
    throw new ApiError(message, error); 
  }
};

export const updateStudentApi = async (student: Student): Promise<Student> => {
  try {
    const response: AxiosResponse<Student> = await axiosInstance.patch(`${STUDENT_API_ENDPOINT}/${student.id}`, student);
    return response.data;
  } catch (error: any) {
    console.error('Error updating student:', error);
    const message = extractErrorMessage(error);
    throw new ApiError(message, error);
  }
};

export const deleteStudentApi = async (id: number): Promise<number> => {
  try {
    await axiosInstance.delete(`${STUDENT_API_ENDPOINT}/${id}`);
    return id;
  } catch (error: any) {
    console.error('Error deleting student:', error);
    const message = extractErrorMessage(error);
    throw new ApiError(message, error);
  }
};

export const fetchAcademicYearApi = async () => {
  try {
    const response: AxiosResponse<any> = await axiosInstance.get(`${ACADEMICYEAR_API_ENDPOINT}`);
    return response.data;
  } catch (error: any) {
    console.log('Error fetch academic year:', error);
    const message = extractErrorMessage(error);
    throw new ApiError(message, error);
  }
}